import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { ChevronDown, ChevronUp, Check, Circle } from 'lucide-react';
import AdminFeatureCard from './AdminFeatureCard';

const FeatureManagement = ({
    selectedOrg,
    features = [],
    expandedFeatures = {},
    setExpandedFeatures = () => {},
    adminComments = {},
    adminNotes = {},
    handleAddComment,
    handleAddNote,
    handleDeleteComment,
    handleDeleteNote
}) => {
    const [currentSection, setCurrentSection] = useState(null);
    const [responses, setResponses] = useState([]);
    const [comments, setComments] = useState({});
    const [dependencies, setDependencies] = useState({});
    const [conflicts, setConflicts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get unique sections
    const sections = useMemo(() => {
        const uniqueSections = [...new Set(features.map(feature => feature.section))];
        return uniqueSections.map(section => ({
            id: section?.toLowerCase().replace(/\s+/g, '-') || 'uncategorized',
            name: section || 'Uncategorized'
        }));
    }, [features]);

    // Get features for current section
    const currentFeatures = useMemo(() => {
        if (!currentSection) return [];
        return features.filter(feature => 
            feature.section === sections.find(s => s.id === currentSection)?.name
        );
    }, [features, currentSection, sections]);

    // Calculate section progress
    const sectionProgress = useMemo(() => {
        return sections.map(section => {
            const sectionFeatures = features.filter(f => f.section === section.name);
            const responded = sectionFeatures.filter(f => 
                responses.some(r => 
                    r.feature_reference === f.reference && 
                    r.organization_id === selectedOrg?.id
                )
            ).length;
            
            return {
                ...section,
                total: sectionFeatures.length,
                completed: responded,
                percentage: Math.round((responded / sectionFeatures.length) * 100)
            };
        });
    }, [sections, features, responses, selectedOrg?.id]);

    // Load data
    useEffect(() => {
        const loadData = async () => {
            if (!selectedOrg?.id) return;
            setLoading(true);
            setError(null);

            try {
                const [responsesRes, commentsRes, depsRes] = await Promise.all([
                    fetch(`/api/responses/${selectedOrg.id}`, { credentials: 'include' }),
                    fetch(`/api/comments/${selectedOrg.id}`, { credentials: 'include' }),
                    fetch('/api/features/dependencies/all', { credentials: 'include' })
                ]);

                if (!responsesRes.ok || !commentsRes.ok || !depsRes.ok) {
                    throw new Error('Failed to fetch data');
                }

                const [responsesData, commentsData, depsData] = await Promise.all([
                    responsesRes.json(),
                    commentsRes.json(),
                    depsRes.json()
                ]);

                setResponses(responsesData);
                setComments(commentsData);

                // Process dependencies
                const depsMap = {};
                depsData.forEach(dep => {
                    if (!depsMap[dep.feature_reference]) {
                        depsMap[dep.feature_reference] = [];
                    }
                    depsMap[dep.feature_reference].push(dep);
                });
                setDependencies(depsMap);

            } catch (error) {
                console.error('Error loading data:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
        
        // Set initial section if none selected
        if (!currentSection && sections.length > 0) {
            setCurrentSection(sections[0]?.id);
        }
    }, [selectedOrg?.id, sections]);

    if (loading) {
        return <div className="text-center p-8">Loading features...</div>;
    }

    if (error) {
        return <div className="text-red-600 p-8">Error: {error}</div>;
    }

    return (
        <div className="lg:flex parent-container">
            {/* Left Sidebar - Section Navigation */}
            <div className="lg:w-1/3 left-column">
                <div className="space-y-4">
                    {sections.map((section) => {
                        const progress = sectionProgress.find(s => s.id === section.id);
                        return (
                            <button
                                key={section.id}
                                onClick={() => setCurrentSection(section.id)}
                                className={`w-full flex items-center gap-4 p-4 rounded-lg transition-colors
                                    ${currentSection === section.id
                                        ? 'bg-green-50 border-green-200' 
                                        : 'bg-white hover:bg-gray-50'
                                    } border`}
                            >
                                {progress?.percentage === 100 
                                    ? <Check className="w-5 h-5 text-green-600" />
                                    : <Circle className="w-5 h-5 text-gray-400" />
                                }
                                
                                <div className="flex-1">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="font-medium text-gray-900">{section.name}</span>
                                        <span className="text-sm text-gray-600">
                                            {progress?.completed}/{progress?.total} Complete
                                        </span>
                                    </div>
                                    
                                    <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-green-600 transition-all duration-500"
                                            style={{ width: `${progress?.percentage || 0}%` }}
                                        />
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Right Content - Feature Cards */}
            <div className="lg:w-2/3 lg:pl-8">
                <div className="space-y-6">
                    {currentFeatures.map(feature => (
                        <AdminFeatureCard
                            key={feature.reference}
                            feature={feature}
                            responses={responses}
                            comments={comments[feature.reference] || []}
                            dependencies={dependencies[feature.reference]}
                            conflicts={conflicts[feature.reference]}
                            isExpanded={expandedFeatures[feature.reference]}
                            onToggleExpand={() => setExpandedFeatures(prev => ({
                                ...prev,
                                [feature.reference]: !prev[feature.reference]
                            }))}
                            onAddComment={handleAddComment}
                            onAddNote={handleAddNote}
                            onDeleteComment={handleDeleteComment}
                            onDeleteNote={handleDeleteNote}
                            adminComments={adminComments}
                            adminNotes={adminNotes}
                            organization={selectedOrg}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FeatureManagement;